import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { PAGE_SIZE } from "@shared/utils/constants";
import { HelpCircle } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBillCycleExport, useBillCycleList, useCategoryConfigMap } from "../hooks";

import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { FormField, FormService } from "@shared/services/FormServices";
import { ChevronDown, ChevronUp, Download, Filter, Search } from "lucide-react";
import { logEvent } from "@shared/analytics/analytics";

// Types based on your data structure
interface BillCycle {
  id: string;
  name: string;
  consumerCount: number;
  premiseCount: number;
  lastRunDate: string;
  days: number;
  runCount: number;
  createdBy: string;
  status: "Active" | "Inactive";
  lastRunDetails?: {
    initiatedBy: string;
    revenue: string;
    actualVsEstimate: string;
    errors: number;
  };
}

interface BillCycleTableProps {
  data?: any; // API response data
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

export function BillCycleTable({
  data: propData,
  currentPage = 1,
  onPageChange,
}: BillCycleTableProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchValue] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const { data: categoryOptions } = useCategoryConfigMap();

  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All Status",
        options: [
          { value: "Active", label: "Active" },
          { value: "Inactive", label: "Inactive" },
        ],
        className: "col-span-1", // Grid column span
        classes: {
          // container: "space-y-2",
          // label: "text-sm font-medium text-gray-700",
          // select:
          //   "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-xs mt-1",
        },
      },
      {
        name: "category",
        label: "Consumer Category",
        type: "select",
        placeholder: "All Categories",
        options: categoryOptions?.map((category: any) => ({
          value: category.id,
          label: category.name,
        })),
        className: "col-span-1",
        classes: {
          // container: "space-y-2",
          // label: "text-sm font-medium text-gray-700",
          // select:
          //   "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-xs mt-1",
        },
      },

      // Row 2: Date Range Filters (Date From, Date To)
      {
        name: "dateFrom",
        label: "Date From",
        type: "datepicker",
        placeholder: "Select start date",
        className: "col-span-1",
        classes: {
          // container: "space-y-2",
          // label: "text-sm font-medium text-gray-700",
          // input:
          //   "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-xs mt-1",
        },
      },
      {
        name: "dateTo",
        label: "Date To",
        type: "datepicker",
        placeholder: "Select end date",
        className: "col-span-1",
        classes: {
          // container: "space-y-2",
          // label: "text-sm font-medium text-gray-700",
          // input:
          //   "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          // error: "text-red-600 text-xs mt-1",
        },
      },
    ],
    []
  );

  // Create form instance
  const filterForm = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Watch form values for real-time filtering

  const [filters, setFilters] = useState({
    page: currentPage,
    status: "",
    start_date: "",
    end_date: "",
    category: "",
    search_data: "",
  });

  const { data: billCycleList, isLoading: apiLoading } =
    useBillCycleList(filters);

  const exportFilters = useMemo(() => {
    return {
      status: filters.status,
      start_date: filters.start_date,
      end_date: filters.end_date,
      category: filters.category,
      search_data: filters.search_data,
      // Note: No page parameter for export to get all data
    };
  }, [filters]);

  const { exportCSV, isExporting, error, isError } =
    useBillCycleExport(exportFilters);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchValue(value);
    setFilters((prev) => ({
      ...prev,
      search_data: value.length > 2 ? value : "",
    }));
  };

  // Handle form submission
  const handleFilterSubmit = (data: any) => {
    setFilters((prev) => ({
      ...prev,
      search_data: searchTerm,
      status: data.status || "",
      frequency: data.frequency || "",
      consumerCategory: data.consumerCategory || "",
      date_start: data.dateFrom,
      date_end: data.dateTo,
    }));
  };

  // Handle clear all filters
  const handleClearFilters = useCallback(() => {
    filterForm.reset();
    setIsFilterExpanded(!isFilterExpanded);
    setSearchValue("");
    setFilters({
      page: currentPage,
      status: "",
      start_date: "",
      end_date: "",
      category: "",
      search_data: "",
    });
  }, [filterForm, currentPage]);

  const isLoading = propData ? false : apiLoading;
  const cycles = billCycleList?.results || [];
  const totalCount = billCycleList?.count || 0;

  // Handlers
  const handleViewBillCycle = (cycle: BillCycle) => {
    navigate(`detail/${cycle.id}`);
  };

  const handleEditBillCycle = (cycle: BillCycle) => {
    navigate(`action/edit/${cycle.id}`);
  };

  const handleRunNow = (cycle: BillCycle) => {
    console.log(`Run ${cycle.name}`);
  };

  const handleExport = () => {
    exportCSV();
    logEvent('BX Bill Cycle List Exported');
  };

  const handleRefresh = () => {
    const key = QueryKeyFactory.module.bx.billCycle.billCycleList();

    queryClient.invalidateQueries({
      queryKey: key,
    });
    queryClient.refetchQueries({
      queryKey: key,
    });
  };

  const columns = [
    {
      key: "billCycleName",
      header: "Bill Cycle Name",
      render: (value: string, row: BillCycle) => (
        <button
          onClick={() => handleViewBillCycle(row)}
          className="text-left font-medium hover:text-blue-700 cursor-pointer"
        >
          {value}
        </button>
      ),
    },
    {
      key: "consumerCount",
      header: "Consumer Count",
      render: (value: number) => <div>{value.toLocaleString()}</div>,
    },
    {
      key: "consumerCoverage",
      header: "Consumer Coverage",
      render: (value: number) => <div>{value || "NA"}</div>,
    },
    {
      key: "premise",
      header: "Premise Count",
      render: (value) => <div>{value.length}</div>,
    },
    {
      key: "lastRunDate",
      header: "Last Run Date",
    },
    {
      key: "daysSinceLastRun",
      header: (
        <div className="flex items-center gap-1">
          Days
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle size={14} className="text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Billing duration per cycle</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      key: "billRunCount",
      header: "Run Count",
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      sortable: true,
    },
    {
      key: "isActive",
      header: "Status",
      render: (value: any) => (
        <div>{value === true ? "Active" : "Inactive"}</div>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View",
      onClick: (row: BillCycle) => {
        handleViewBillCycle(row);
      },
    },
    {
      label: "Edit",
      onClick: (row: BillCycle) => {
        handleEditBillCycle(row);
      },
    },
  ];

  return (
    <div className=" overflow-hidden">
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Search Bar */}
          <div className="relative w-full md:w-auto md:flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <Input
              placeholder="Search by bill cycle name, code..."
              className="pl-10"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Actions Group */}
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {/* Filter Toggle Button */}
            <Button
              variant="outline"
              className={`flex gap-1`}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              <Filter size={16} />
              Filters
              {isFilterExpanded ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </Button>

            <Button
              variant="outline"
              className="flex gap-1"
              onClick={handleExport}
              disabled={isLoading}
            >
              <Download size={16} />
              Export
            </Button>
          </div>
        </div>
        {/* Expandable Filter Section */}
        {isFilterExpanded && (
          <div className="mt-4 pt-4 border rounded-md p-4">
            <DynamicForm
              fields={filterFields}
              form={filterForm}
              onSubmit={handleFilterSubmit}
              gridColumns={4} // 3 columns for primary filters
              config={{
                layout: "vertical",
                showSubmitButton: false,
                showResetButton: false,
                classes: {
                  form: "grid", // Grid layout with gap
                  fieldContainer: "w-full",
                },
              }}
            />

            {/* Filter Actions */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClearFilters}
                disabled={isLoading}
                size="sm"
              >
                Clear All
              </Button>
              <Button
                type="button"
                onClick={() => handleFilterSubmit(filterForm.getValues())}
                size="sm"
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </div>

      <GenericTable
        data={billCycleList?.result ?? []}
        columns={columns}
        rowKey="id"
        actions={tableActions}
        actionsType="dropdown"
        emptyMessage="No bill cycles found"
        loading={isLoading}
        onExport={handleExport}
        pagination={{
          currentPage,
          totalPages: Math.ceil(totalCount / PAGE_SIZE),
          totalItems: totalCount,
          itemsPerPage: PAGE_SIZE,
          startItem: (currentPage - 1) * PAGE_SIZE + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, totalCount),
        }}
        onPageChange={onPageChange}
        getRowClassName={(row: BillCycle) => "cursor-pointer hover:bg-gray-50"}
      />
    </div>
  );
}
